//frontend/src/pages/SelectSpecialist.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    Container, 
    Typography, 
    Grid, 
    Avatar,
    Box,
    CircularProgress,
    Paper,
    IconButton
} from '@mui/material';
import { 
    Shuffle as ShuffleIcon,
    RadioButtonUnchecked,
    RadioButtonChecked
} from '@mui/icons-material';
import { useBooking } from '../context/BookingContext';
import axiosInstance from '../utils/axios';
import { useTranslation } from 'react-i18next';

export default function SelectSpecialist() {
    const { t } = useTranslation();
    const [specialists, setSpecialists] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { bookingData, setBookingData, setActiveStep } = useBooking();
    const navigate = useNavigate();
    const [selectedSpecialistId, setSelectedSpecialistId] = useState(null);

    useEffect(() => {
        if (!bookingData.service) {
            navigate('/select-service');
            return;
        }
        fetchSpecialists();
        setActiveStep(1);
    }, []);

    const fetchSpecialists = async () => {
        try {
            const response = await axiosInstance.get('/specialists');
            const filteredSpecialists = response.data.filter(specialist => 
                specialist.services?.includes(bookingData.service.name) && 
                specialist.status === 'approved' &&
                specialist.role !== 'superadmin'
            );
            setSpecialists(filteredSpecialists);
        } catch (error) {
            console.error('Error fetching specialists:', error);
            setError('Failed to load specialists. Please try again later.');
        } finally {
            setLoading(false);
        }
    };

    const handleSpecialistSelect = async (specialist) => {
        if (specialist === 'random') {
            const availableSpecialists = specialists.filter(s => 
                s.services.includes(bookingData.service.name)
            );
            if (availableSpecialists.length > 0) {
                specialist = availableSpecialists[Math.floor(Math.random() * availableSpecialists.length)];
            }
        }
        
        setSelectedSpecialistId(specialist._id);
        setBookingData(prev => ({
            ...prev,
            specialist: specialist
        }));
        
        setTimeout(() => {
            setActiveStep(2);
            navigate('/select-datetime');
        }, 150);
    };

    if (loading) return <Container sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Container>;
    if (error) return <Container><Typography color="error" align="center" sx={{ mt: 4 }}>{error}</Typography></Container>;

    return (
        <Container maxWidth="md">
            <Typography variant="h4" gutterBottom align="center" sx={{ mb: 4 }}>
                {t('booking.steps.selectSpecialist')}
            </Typography>

            <Paper
                onClick={() => handleSpecialistSelect('random')} 
                sx={{
                    p: 2,
                    mb: 3,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    '&:hover': { 
                        bgcolor: 'rgba(0,0,0,0.02)',
                        transform: 'translateX(8px)',
                        transition: 'all 0.2s'
                    }
                }}
            >
                <Box 
                    sx={{ 
                        width: 40, 
                        height: 40,
                        bgcolor: 'primary.light',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mr: 2
                    }}
                >
                    <ShuffleIcon color="primary" />
                </Box>
                <Typography variant="subtitle1">
                    {t('common.anySpecialist')}
                </Typography>
            </Paper>

            <Grid container spacing={2}>
                {specialists.map((specialist) => (
                    <Grid item xs={12} key={specialist._id}>
                        <Paper
                            onClick={() => handleSpecialistSelect(specialist)}
                            sx={{
                                p: 2,
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                '&:hover': { 
                                    bgcolor: 'rgba(0,0,0,0.02)',
                                    transform: 'translateX(8px)',
                                    transition: 'all 0.2s'
                                }
                            }}
                        >
                            <Avatar 
                                sx={{ 
                                    width: 48, 
                                    height: 48,
                                    mr: 2,
                                    bgcolor: (theme) => theme.palette.primary.light,
                                    fontSize: '1.5rem'
                                }}
                                src={specialist.image}
                            >
                                {specialist.name[0].toUpperCase()}
                            </Avatar>
                            <Box sx={{ flex: 1 }}>
                                <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
                                    {specialist.name}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {specialist?.services?.join(', ')}
                                </Typography>
                            </Box>
                            {specialist._id === selectedSpecialistId ? 
                                <RadioButtonChecked sx={{ color: 'primary.main' }} /> :
                                <RadioButtonUnchecked sx={{ color: 'text.disabled' }} />
                            }
                        </Paper>
                    </Grid>
                ))}
            </Grid>
        </Container>
    );
}